<template>
  <div class="w-full">
    <!-- Loading -->
    <div v-if="loading" class="flex justify-center items-center py-12">
      <LoadingComponent />
    </div>

    <!-- Ranking Report -->
    <div v-else class="bg-white rounded-lg shadow-md overflow-hidden">
      <!-- Header -->
      <div class="text-black px-6 py-4">
        <div class="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-3">
          <div>
            <h2 class="text-2xl font-bold">📊 Ranking Report</h2>
            <p class="text-sm mt-1 text-gray-600">Histórico completo da competição</p>
            <p class="text-xs mt-1 text-gray-500">Atualizado em: {{ rankingReport.date }}</p>
          </div>
          <div v-show="hasPermission('AdminBolao')" class="w-full lg:w-auto">
            <button 
              @click="shareRanking" 
              class="w-full lg:w-auto text-white bg-green-600 hover:bg-green-50 hover:text-green-700 font-semibold text-sm px-4 py-2 rounded-lg transition-all duration-200 shadow-sm hover:shadow-md" 
              type="button">
              <i class="fab fa-whatsapp mr-2"></i> Compartilhar
            </button>
          </div>
        </div>
      </div>

      <!-- Legenda -->
      <div class="px-6 py-3 bg-gray-50 border-b text-xs flex flex-wrap gap-4">
        <div class="flex items-center">
          <div class="w-3 h-3 bg-green-100 border border-green-300 mr-1"></div>
          <span>Zona de Premiação</span>
        </div>
        <div class="flex items-center">
          <div class="w-3 h-3 bg-red-100 border border-red-300 mr-1"></div>
          <span>Zona da Lanterna</span>
        </div>
      </div>

      <!-- Tabela Desktop -->
      <div class="hidden lg:block overflow-x-auto">
        <table class="w-full">
          <thead class="bg-gray-100 border-b-2 border-gray-300">
            <tr>
              <th class="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Pos</th>
              <th class="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Participante</th>
              <th class="px-4 py-3 text-center text-xs font-semibold text-gray-700 uppercase">Pontos</th>
              <th class="px-4 py-3 text-center text-xs font-semibold text-gray-700 uppercase">Acertos</th>
              <th class="px-4 py-3 text-center text-xs font-semibold text-gray-700 uppercase">Variação</th>
            </tr>
          </thead>
          <tbody>
            <tr 
              v-for="(user, id) in rankingReport.ranking" 
              :key="user.userId"
              :class="[
                'border-b hover:bg-gray-50 transition-colors',
                getBgRows(id, rankingReport.ranking)
              ]">
              <!-- Posição -->
              <td class="px-6 py-3">
                <div class="flex items-center">
                  <span class="font-bold text-2xl text-green-700">{{ id + 1 }}º</span>
                </div>
              </td>
              
              <!-- Nome -->
              <td class="px-6 text-left py-3">
                <span class="font-medium text-gray-900">{{ user.userName }}</span>
              </td>
              
              <!-- Pontos -->
              <td class="px-4 py-3 text-center">
                <span class="font-bold text-xl text-green-600">{{ user.totalPoints !== undefined ? user.totalPoints : 0 }}</span>
              </td>
              
              <!-- Acertos -->
              <td class="px-4 py-3 text-center">
                <span class="inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold bg-green-100 text-green-800">
                  {{ user.totalHits !== undefined ? user.totalHits : 0 }}
                </span>
              </td>
              
              <!-- Variação -->
              <td class="px-4 py-3 text-center">
                <div v-html="getPosition(user)" class="text-sm font-semibold"></div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Cards Mobile -->
      <div class="lg:hidden">
        <div 
          v-for="(user, id) in rankingReport.ranking" 
          :key="user.userId"
          :class="[
            'border-b hover:bg-gray-50 transition-colors',
            getBgRows(id, rankingReport.ranking)
          ]">
          <!-- Linha compacta com informações -->
          <div class="flex items-center justify-between py-3 px-4">
            <!-- Posição -->
            <div class="flex items-center justify-center w-10">
              <span class="font-bold text-lg text-green-700">{{ id + 1 }}º</span>
            </div>
            
            <!-- Nome -->
            <div class="flex-1 min-w-0 mx-3">
              <div class="font-medium text-gray-900 text-xs">{{ user.userName }}</div>
            </div>
            
            <!-- Pontos e Acertos -->
            <div class="flex items-center gap-2">
              <span class="font-bold text-base text-green-600">
                {{ user.totalPoints !== undefined ? user.totalPoints : 0 }}
              </span>
              <span class="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-green-700 text-white">
                +{{ user.totalHits !== undefined ? user.totalHits : 0 }}
              </span>
              <div v-html="getPosition(user)" class="text-xs ml-2"></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import RankingService from '@/services/rankingService';
import WhatsAppService from '@/services/whatsappService';
import LoadingComponent from '@/components/LoadingComponent.vue';

export default {
  name: 'RankingReportTable',
  components: {
    LoadingComponent,
  },
  props: [
    'userData'
  ],
  mounted() {
    RankingService.getRankingReport().then(data => {
      this.rankingReport = data;
      this.loading = false;
    });
  },
  methods: {
    getBgRows(id, ranking) {
      if (id <= 7) {
        return "border-l-4 border-green-500 bg-green-50"
      }

      let total = ranking.length;
      if (id >= (total - 1)) {
        return "border-l-4 border-red-500 bg-red-50"
      }
      return "bg-white"
    },
    getPosition(user) {
      let position = user.lastPosition - user.position;

      if (user.lastPosition == 0) {
        position = 0
      }

      if (position > 0) {
        return `<span class="text-green-700"><i class="fas fa-sort-up"></i>${position}</span>`;
      }

      if (position < 0) {
        return `<span class="text-red-700"><i class="fas fa-sort-down"></i>${position * -1}</span>`;
      }

      return `<span class="text-grey-700"><i class="fas fa-minus-circle"></i> </span>`;
    },
    hasPermission(permission) {
      if (this.userData.length == 0) {
        return false;
      }
      let { rules } = this.userData;
      return rules.filter(data => {
        return data === permission || data === "SuperAdmin"
      }).length > 0;
    },
    shareRanking() {
      WhatsAppService.shareRanking(this.rankingReport.ranking, true);
    },
  },
  data(){
    return {
      rankingReport: [],
      loading: true,
    }
  },
}
</script>
<style scoped>
/* Animação suave ao hover */
tr:hover {
  transform: scale(1.001);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

/* Responsive table */
@media (max-width: 1024px) {
  table {
    font-size: 0.875rem;
  }
}
</style>
